'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className = '' }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState(2026);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    products: [
      { label: 'KnowMax', href: '/product-suite-overview#knowmax' },
      { label: 'PhygitalMax', href: '/product-suite-overview#phygitalmax' },
      { label: 'LeapMax', href: '/product-suite-overview#leapmax' },
      { label: 'DeviceMax', href: '/product-suite-overview#devicemax' },
    ],
    solutions: [
      { label: 'Telecommunications', href: '/industry-solutions#telecom' },
      { label: 'Banking & Finance', href: '/industry-solutions#banking' },
      { label: 'Retail & E-commerce', href: '/industry-solutions#retail' },
    ],
    company: [
      { label: 'About Us', href: '#' },
      { label: 'Careers', href: '#' },
      { label: 'Contact', href: '#' },
      { label: 'Partners', href: '#' },
    ],
    resources: [
      { label: 'Documentation', href: '#' },
      { label: 'Case Studies', href: '#' },
      { label: 'Blog', href: '#' },
      { label: 'Support', href: '#' },
    ],
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: 'LinkIcon', href: '#' },
    { name: 'Twitter', icon: 'AtSymbolIcon', href: '#' },
    { name: 'YouTube', icon: 'PlayIcon', href: '#' },
    { name: 'GitHub', icon: 'CodeBracketIcon', href: '#' },
  ];

  if (!isHydrated) {
    return (
      <footer className={`bg-black text-white pt-16 pb-8 ${className}`}>
        <div className="container mx-auto px-6 lg:px-8">
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto" />
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`bg-black text-white pt-16 pb-8 ${className}`}>
      <div className="container mx-auto px-6 lg:px-8">

        {/* Top divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-16" />

        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-10 mb-16">
          {/* Brand Section */}
          <div className="lg:col-span-2 space-y-6">
            <Link href="/homepage4" className="flex items-center space-x-3 group">
              <div className="relative">
                <svg
                  width="44"
                  height="44"
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="transition-transform duration-300 group-hover:scale-105"
                >
                  <rect width="40" height="40" rx="4" fill="white" fillOpacity="0.06" />
                  <path d="M20 8L28 14V26L20 32L12 26V14L20 8Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20 14L24 17V23L20 26L16 23V17L20 14Z" fill="white" opacity="0.7" />
                  <circle cx="20" cy="20" r="2" fill="white" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="font-display font-bold text-xl leading-none text-white">UnifyMax</span>
                <span className="font-body text-xs text-white/40 leading-none mt-1">Enterprise</span>
              </div>
            </Link>
            <p className="font-body text-sm text-white/40 leading-relaxed max-w-[260px]">
              Transforming customer engagement with 25+ years of expertise and AI-powered intelligence.
            </p>
            <div className="flex space-x-2">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-9 h-9 bg-white/6 rounded-lg flex items-center justify-center hover:bg-white/12 hover:text-[#1bb4d7] transition-all duration-300"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={16} variant="outline" />
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-display font-semibold text-sm text-white/60 uppercase tracking-widest mb-5">Products</h3>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="font-body text-sm text-white/50 hover:text-white transition-colors duration-300">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="font-display font-semibold text-sm text-white/60 uppercase tracking-widest mb-5">Solutions</h3>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="font-body text-sm text-white/50 hover:text-white transition-colors duration-300">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-display font-semibold text-sm text-white/60 uppercase tracking-widest mb-5">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="font-body text-sm text-white/50 hover:text-white transition-colors duration-300">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-display font-semibold text-sm text-white/60 uppercase tracking-widest mb-5">Resources</h3>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} className="font-body text-sm text-white/50 hover:text-white transition-colors duration-300">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="font-body text-xs text-white/30">
              © {currentYear} UnifyMax Enterprise. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              {[
                { label: 'Privacy Policy', href: '/privacy' },
                { label: 'Terms of Service', href: '/terms' },
                { label: 'Security', href: '/security' },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="font-body text-xs text-white/30 hover:text-white/70 transition-colors duration-300"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
